import React, { useCallback, useState } from "react"
import { useMobergMenu } from "../../../../../../Hooks/useMobergMenu"
import { MobergContextMenuItem } from "../../../../../../Components/MobergMenu/MobergContextMenuItem"
import { MobergMenuDivider } from "../../../../../../Components/MobergMenu/MobergMenuDivider"
import { MobergIconSize } from "../../../../../../Moberg"
import { MdVisibility, MdVisibilityOff } from "react-icons/md"
import { EEGChannelDisplayInfo } from "../../Types/EEG"

type UseChannelVisibilityContextMenuProps = {
	channels: EEGChannelDisplayInfo[]
	hiddenChannelKeys: Set<string>
	onHiddenChannelsChange: (hiddenKeys: Set<string>) => void
}

export const useChannelVisibilityContextMenu = ({
	channels,
	hiddenChannelKeys,
	onHiddenChannelsChange,
}: UseChannelVisibilityContextMenuProps) => {
	const [menuChannelKey, setMenuChannelKey] = useState<string | null>(null)
	const menu = useMobergMenu({ placement: "bottom-start", isContextMenu: true })

	const menuChannel = channels.find(c => c.key === menuChannelKey)
	const isChannelHidden = menuChannelKey ? hiddenChannelKeys.has(menuChannelKey) : false

	const openChannelContextMenu = useCallback(
		(event: React.MouseEvent, channelKey: string) => {
			setMenuChannelKey(channelKey)
			menu.open(event)
		},
		[menu]
	)

	const handleToggleChannel = useCallback(() => {
		if (!menuChannelKey) return

		const nextHiddenKeys = new Set(hiddenChannelKeys)
		if (nextHiddenKeys.has(menuChannelKey)) {
			nextHiddenKeys.delete(menuChannelKey)
		} else {
			nextHiddenKeys.add(menuChannelKey)
		}
		onHiddenChannelsChange(nextHiddenKeys)
		menu.close()
	}, [hiddenChannelKeys, menu, menuChannelKey, onHiddenChannelsChange])

	const handleShowAll = useCallback(() => {
		onHiddenChannelsChange(new Set())
		menu.close()
	}, [menu, onHiddenChannelsChange])

	const handleHideAll = useCallback(() => {
		const allKeys = new Set(channels.map(c => c.key))
		onHiddenChannelsChange(allKeys)
		menu.close()
	}, [channels, menu, onHiddenChannelsChange])

	const handleShowOnlyThisChannel = useCallback(() => {
		if (!menuChannelKey) return

		const allKeysExceptThis = new Set(channels.filter(c => c.key !== menuChannelKey).map(c => c.key))
		onHiddenChannelsChange(allKeysExceptThis)
		menu.close()
	}, [channels, menu, menuChannelKey, onHiddenChannelsChange])

	const ChannelVisibilityContextMenu = (
		<menu.MobergMenu>
			<MobergContextMenuItem
				onClick={handleToggleChannel}
				icon={isChannelHidden ? <MdVisibility size={MobergIconSize.SMALL} /> : <MdVisibilityOff size={MobergIconSize.SMALL} />}
			>
				{isChannelHidden ? `Show ${menuChannel?.label ?? menuChannelKey}` : `Hide ${menuChannel?.label ?? menuChannelKey}`}
			</MobergContextMenuItem>
			<MobergContextMenuItem onClick={handleShowOnlyThisChannel} icon={<MdVisibility size={MobergIconSize.SMALL} />}>
				Only show {menuChannel?.label ?? menuChannelKey}
			</MobergContextMenuItem>
			<MobergMenuDivider />
			<MobergContextMenuItem onClick={handleShowAll} icon={<MdVisibility size={MobergIconSize.SMALL} />}>
				Show all channels
			</MobergContextMenuItem>
			<MobergContextMenuItem onClick={handleHideAll} icon={<MdVisibilityOff size={MobergIconSize.SMALL} />}>
				Hide all channels
			</MobergContextMenuItem>
		</menu.MobergMenu>
	)

	return { ChannelVisibilityContextMenu, openChannelContextMenu }
}
